/**
 * WordPress dependencies
 */
import { useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import { HelpIcon } from '@nab/components';

/**
 * Internal dependencies
 */
import './style.scss';
import { CircularProgress } from '../circular-progress';

export function OptimizationScore(): JSX.Element {
	const { hasEverCreatedExperiments, isLoading, value } = useSelect(
		( select ) => ( {
			hasEverCreatedExperiments:
				select( NAB_DATA ).hasEverCreatedExperiments(),
			isLoading: ! select( NAB_DATA ).isRecommendationEngineDataLoaded(),
			value: select( NAB_DATA ).getOptimizationScore().value,
		} ),
		[]
	);

	const [ color, title, explanation ] = getLabels( value, {
		hasEverCreatedExperiments,
		isLoading,
	} );

	return (
		<div className="nab-optimization-score">
			<h3 className="nab-overview-component-title">
				{ _x( 'Optimization Score', 'text', 'nelio-ab-testing' ) }
				<HelpIcon
					text={ [
						_x(
							'The Optimization Score measures how effectively you’re improving your website through experimentation. It’s based on your testing habits.',
							'text',
							'nelio-ab-testing'
						),
						_x(
							'Follow the recommended tests to improve your score over time.',
							'user',
							'nelio-ab-testing'
						),
					].join( ' ' ) }
				/>
			</h3>
			<CircularProgress
				color={ color }
				value={ value }
				percentage={ value }
				title={ title }
				explanation={ explanation }
			/>
		</div>
	);
}

// HELPERS

function getLabels(
	score: number,
	{
		hasEverCreatedExperiments,
		isLoading,
	}: {
		readonly hasEverCreatedExperiments: boolean;
		readonly isLoading: boolean;
	}
): [ 'disabled' | 'bad' | 'ok' | 'good', string, string ] {
	if ( isLoading ) {
		return [
			'disabled',
			_x( 'Loading…', 'text', 'nelio-ab-testing' ),
			_x(
				'Loading optimization score. Please wait…',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	if ( ! hasEverCreatedExperiments ) {
		return [
			'bad',
			_x(
				'Getting started',
				'text (optimization score)',
				'nelio-ab-testing'
			),
			_x(
				'It’s time to launch your first meaningful tests.',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	if ( score <= 30 ) {
		return [
			'bad',
			_x(
				'Building Momentum',
				'text (optimization score)',
				'nelio-ab-testing'
			),
			_x(
				'You’re building good testing habits. Keep the momentum going!',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	if ( score <= 70 ) {
		return [
			'ok',
			_x(
				'Making Progress',
				'text (optimization score)',
				'nelio-ab-testing'
			),
			_x(
				'You’re testing consistently. Focus on your highest-impact opportunities.',
				'text',
				'nelio-ab-testing'
			),
		];
	}

	return [
		'good',
		_x(
			'Raising the Bar',
			'text (optimization score)',
			'nelio-ab-testing'
		),
		_x(
			'Your experimentation program is strong. Keep refining and challenging your assumptions.',
			'text',
			'nelio-ab-testing'
		),
	];
}
